import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Heart, MapPin, Bed, Maximize, Euro, Trash2 } from "lucide-react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FavoritesList = () => {
  const { token } = useAuth();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFavorites();
  }, []);

  const fetchFavorites = async () => {
    try {
      const response = await axios.get(`${API}/favorites`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFavorites(response.data);
    } catch (error) {
      console.error("Error fetching favorites:", error);
    } finally {
      setLoading(false);
    }
  };

  const removeFavorite = async (listingId) => {
    try {
      await axios.post(`${API}/favorites/${listingId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFavorites(favorites.filter(f => f.id !== listingId));
      toast.success("Eliminat din favorite");
    } catch (error) {
      console.error("Error removing favorite:", error);
      toast.error("Eroare la eliminare");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-red-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (favorites.length === 0) {
    return (
      <div className="text-center py-12">
        <Heart className="w-16 h-16 text-stone-300 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-stone-700 mb-2">Nicio proprietate salvată</h3>
        <p className="text-stone-500 mb-4">Salvează proprietățile care îți plac pentru a le găsi mai ușor</p>
        <Link to="/">
          <Button className="bg-emerald-600 hover:bg-emerald-700">
            Explorează anunțuri
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {favorites.map((listing) => (
        <div
          key={listing.id}
          className="bg-white rounded-xl border border-stone-200 overflow-hidden hover:shadow-lg transition-shadow"
        >
          <div className="flex">
            {/* Image */}
            <Link to={`/proprietate/${listing.id}`} className="w-32 h-32 flex-shrink-0">
              <img
                src={listing.images?.[0] || "https://images.pexels.com/photos/1396122/pexels-photo-1396122.jpeg"}
                alt={listing.title}
                className="w-full h-full object-cover"
              />
            </Link>

            {/* Content */}
            <div className="flex-1 p-3 flex flex-col justify-between">
              <div>
                <Link 
                  to={`/proprietate/${listing.id}`}
                  className="font-semibold text-stone-800 hover:text-emerald-700 line-clamp-1"
                >
                  {listing.title}
                </Link>
                
                <div className="flex items-center gap-1 text-stone-500 text-sm mt-1">
                  <MapPin className="w-3 h-3" />
                  <span className="line-clamp-1">{listing.zone || listing.city}</span>
                </div>

                <div className="flex items-center gap-3 mt-2 text-stone-600 text-sm">
                  {listing.rooms && (
                    <span className="flex items-center gap-1">
                      <Bed className="w-3 h-3" />
                      {listing.rooms}
                    </span>
                  )}
                  {listing.surface && (
                    <span className="flex items-center gap-1">
                      <Maximize className="w-3 h-3" />
                      {listing.surface} mp
                    </span>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between mt-2">
                <Badge className="bg-emerald-100 text-emerald-700 font-bold">
                  <Euro className="w-3 h-3 mr-1" />
                  {listing.price?.toLocaleString('ro-RO')} {listing.currency || 'EUR'}
                </Badge>

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeFavorite(listing.id)}
                  className="text-red-500 hover:text-red-600 hover:bg-red-50 p-1 h-auto"
                  data-testid={`remove-favorite-${listing.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default FavoritesList;
